import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/core/auth/hooks/useAuth";
import { Button } from "@/shared/components/shadcn/Button";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { LoadingSpinner } from "@/shared/components/ui/LoadingSpinner";
import { Page, PageContent } from "@/shared/components/ui/Page";
import { PageHeader } from "@/shared/components/ui/PageHeader";
import { Section } from "@/shared/components/ui/Section";
import { captureSquareCallback, completeSquareCallback, discardSquareCallback, matchSquareCallback, type SquareCallbackOutcome } from "../services/squareCallback";
import { squareSettingsPath } from "../services/squareBrowserFlow";
import { squareConflictMessage } from "../services/squareErrors";

export function SquareCallbackPage() {
  const [callback] = useState(captureSquareCallback);
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [outcome, setOutcome] = useState<(SquareCallbackOutcome & { returnPath: string }) | null>(null);

  useEffect(() => {
    if (loading) return;
    let active = true;
    const attempt = user ? matchSquareCallback(callback, user.id) : null;
    if (!attempt) {
      discardSquareCallback(callback);
      return;
    }
    void completeSquareCallback(callback, attempt).then(({ result, conflict }) => {
      if (!active) return;
      const returnPath = squareSettingsPath(attempt);
      setOutcome({ result, conflict, returnPath });
      if (attempt.sameTab && result === "connected") {
        navigate(returnPath, { replace: true });
        return;
      }
      try {
        if (window.opener) {
          window.opener.postMessage({ type: "froppio:square:complete", attemptId: attempt.attemptId, result }, window.location.origin);
          if (!conflict) window.close();
        }
      } catch { /* A severed opener must not prevent returning to settings. */ }
    });
    return () => { active = false; };
  }, [callback, loading, user, navigate]);

  const matchingAttempt = user ? matchSquareCallback(callback, user.id) : null;
  const invalid = !loading && !matchingAttempt;
  return <Page><PageHeader title="Square connection" /><PageContent>
    <Section title="Finish connecting Square" card>
      {loading ? <LoadingSpinner message="Checking your session..." /> : !user ? <>
        <ErrorState message="Sign in with the account that started this connection, then restart Square authorization from settings." />
        <Button onClick={() => navigate("/login", { replace: true })}>Sign in and restart</Button>
      </> : invalid ? <>
        <ErrorState message="This Square connection attempt is missing, expired or belongs to another account. Please restart from Square settings." />
        <Button onClick={() => navigate("/", { replace: true })}>Return to Froppio</Button>
      </> : !outcome ? <LoadingSpinner message="Finishing Square authorization..." /> : <>
        {outcome.result === "connected" ? <p role="status">Square is connected. Payments are still unavailable in Froppio.</p> :
          <ErrorState message={outcome.conflict ? squareConflictMessage : outcome.result === "cancelled" ? "Square connection was cancelled." : "Couldn't finish connecting Square. Please restart from settings."} />}
        {outcome.returnPath && <Button onClick={() => navigate(outcome.returnPath!, { replace: true })}>Return to Square settings</Button>}
      </>}
    </Section>
  </PageContent></Page>;
}
